import type { UseQueryResult } from '@tanstack/react-query'
import type { OutreachPreview } from '@/api/client'
import { fmtCount } from '@/lib/creators'
import { Field, Input, Segmented, Select, Skeleton, errorText } from '@/ui'
import { PreviewLine, type StepProps } from './StepTarget'
import type { SplitMethod } from './wizard'

const METHODS: { value: SplitMethod; label: string }[] = [
  { value: 'one', label: '하나로' },
  { value: 'followers', label: '팔로워 구간' },
  { value: 'random', label: '무작위 A/B/C/D' },
]

const HELP: Record<SplitMethod, string> = {
  one: '모두에게 같은 방식으로 보낸다.',
  followers: '팔로워 수로 나눠 구간마다 다른 메시지를 쓴다. 경계는 1~3개다.',
  random: '똑같이 무작위로 나눠 어느 쪽 반응이 좋은지 견준다. 같은 설정이면 같은 사람이 같은 칸에 들어간다.',
}

/** Step 2 — 나누기: one group, follower bands, or an even random split. */
export function StepSplit({
  s,
  set,
  preview,
  problem,
}: StepProps & { preview: UseQueryResult<OutreachPreview>; problem: string | null }) {
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <div className="text-meta font-medium">나누는 방법</div>
        <Segmented label="나누는 방법" value={s.method} onChange={(method) => set({ method })} options={METHODS} />
        <p className="text-meta text-muted-foreground">{HELP[s.method]}</p>
      </div>
      {s.method === 'followers' && (
        <Field label="경계 (팔로워 수)" hint="쉼표로 나눠 작은 수부터. 5000, 50000이면 5천 미만 · 5천~4만9천 · 5만 이상." error={problem ?? undefined}>
          <Input value={s.boundaries} onChange={(e) => set({ boundaries: e.target.value })} inputMode="numeric" />
        </Field>
      )}
      {s.method === 'random' && (
        <Field label="그룹 수">
          <Select value={s.randomGroups} onChange={(e) => set({ randomGroups: Number(e.target.value) })} className="max-w-[10rem]">
            {[2, 3, 4].map((n) => (
              <option key={n} value={n}>
                {n}개 ({'ABCD'.slice(0, n).split('').join('/')})
              </option>
            ))}
          </Select>
        </Field>
      )}
      <div className="rounded-md bg-muted/60 p-3">
        <PreviewLine q={preview} problem={s.method === 'followers' ? null : problem} />
      </div>
      {preview.error && !preview.data ? null : !preview.data ? (
        <Skeleton className="h-24" />
      ) : (
        <ul aria-label="그룹" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {preview.data.groups.map((g) => (
            <li key={g.key} className="min-w-0 rounded-lg border border-border bg-card p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate font-semibold">{g.label}</span>
                <span className="tabular-nums text-body">{fmtCount(g.count)}명</span>
              </div>
              {g.sample.length > 0 ? (
                <p className="mt-1.5 truncate text-meta text-muted-foreground">
                  예: {g.sample.map((p) => p.display_name || `@${p.handle}`).join(', ')}
                </p>
              ) : (
                <p className="mt-1.5 text-meta text-muted-foreground">사람이 없다.</p>
              )}
            </li>
          ))}
        </ul>
      )}
      {preview.error && preview.data && <p role="alert" className="text-meta text-status-failed">{errorText(preview.error)}</p>}
    </div>
  )
}
